/** Words and numbers of the owners' daily report (MD-0128). Pure, so the wording is tested. */
import type { OwnerDayReport, OwnerStoreDay } from '@/api/ownerReport';

const DAY = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const TO_LAST = [
  'к прошлому воскресенью',
  'к прошлому понедельнику',
  'к прошлому вторнику',
  'к прошлой среде',
  'к прошлому четвергу',
  'к прошлой пятнице',
  'к прошлой субботе',
];
const LAST = [
  'в прошлое воскресенье',
  'в прошлый понедельник',
  'в прошлый вторник',
  'в прошлую среду',
  'в прошлый четверг',
  'в прошлую пятницу',
  'в прошлую субботу',
];
const AVG = [
  'среднее воскресенье',
  'средний понедельник',
  'средний вторник',
  'средняя среда',
  'средний четверг',
  'средняя пятница',
  'средняя суббота',
];
const WORST = [
  'самое слабое воскресенье',
  'самый слабый понедельник',
  'самый слабый вторник',
  'самая слабая среда',
  'самый слабый четверг',
  'самая слабая пятница',
  'самая слабая суббота',
];
const BEST = [
  'лучшее воскресенье',
  'лучший понедельник',
  'лучший вторник',
  'лучшая среда',
  'лучший четверг',
  'лучшая пятница',
  'лучшая суббота',
];

export const num = (value: string | number): number => Number(value);

const whole = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const cents = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const oneDecimal = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** 26 707 */
export function zl(value: string | number): string {
  return whole.format(Math.round(num(value)));
}

export function zlCents(value: string | number): string {
  return cents.format(num(value));
}

/** +1 404 / −35 */
export function signedZl(value: number): string {
  const rounded = Math.round(value);
  return `${rounded >= 0 ? '+' : '−'}${whole.format(Math.abs(rounded))}`;
}

/** +5,5% / −35%; empty when there is nothing to compare with. */
export function change(current: string | number, prior: string | number, decimals = 0): string {
  const base = num(prior);
  if (!base) return '';
  const pct = ((num(current) - base) / base) * 100;
  const shown = decimals
    ? oneDecimal.format(Math.abs(pct))
    : whole.format(Math.round(Math.abs(pct)));
  if (shown === '0' || shown === '0,0') return '0%';
  return `${pct > 0 ? '+' : '−'}${shown}%`;
}

export function trend(current: string | number, prior: string | number): 'up' | 'down' | 'flat' {
  const delta = num(current) - num(prior);
  if (Math.abs(delta) < 0.5) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function weekday(date: string): number {
  return new Date(`${date}T12:00:00Z`).getUTCDay();
}

export function dayTitle(date: string): string {
  const [, month, day] = date.split('-');
  return `${DAY[weekday(date)] ?? ''} ${day}.${month}`;
}

export const toLastWeekday = (date: string) => TO_LAST[weekday(date)] ?? '';
export const lastWeekday = (date: string) => LAST[weekday(date)] ?? '';

/** «в 1,8 раза больше» / «на 35% меньше» against the store's usual day. */
export function versusUsual(amount: string, usual: string): string {
  const ratio = num(amount) / num(usual);
  if (ratio >= 1.5) return `в ${oneDecimal.format(ratio)} раза больше`;
  const pct = Math.round(Math.abs(ratio - 1) * 100);
  return `на ${pct}% ${ratio >= 1 ? 'больше' : 'меньше'}`;
}

export interface AlertView {
  store: OwnerStoreDay;
  kind: 'drop' | 'record';
  headline: string;
  details: string[];
}

export function alertViews(report: OwnerDayReport): AlertView[] {
  const byId = new Map(report.stores.map((store) => [store.location_id, store]));
  const day = weekday(report.date);
  return report.alerts.flatMap((alert): AlertView[] => {
    const store = byId.get(alert.location_id);
    if (!store?.usual) return [];
    const usual = store.usual;
    const headline = `${zl(store.till.day)} zł, ${AVG[day] ?? ''} ${zl(usual.amount)}: ${versusUsual(store.till.day, usual.amount)}`;
    if (alert.kind === 'record') {
      return [
        {
          store,
          kind: 'record',
          headline,
          details: [`${capitalize(BEST[day] ?? '')} за ${usual.record_weeks} недель`],
        },
      ];
    }
    const avgCheck = store.checks ? num(store.till.day) / store.checks : 0;
    const details = [
      `${capitalize(WORST[day] ?? '')} за ${usual.weeks} недель`,
      `Покупок ${store.checks} вместо ${zl(usual.checks)}, средний чек ${zl(avgCheck)} zł вместо ${zl(usual.avg_check)}`,
    ];
    const absent = store.missing
      .filter((item) => item.last_sale_at === null)
      .map((item) => item.product_name);
    const early = store.missing
      .filter((item) => item.last_sale_at !== null)
      .map((item) => `${item.product_name} с ${item.last_sale_at}`);
    if (absent.length) details.push(`Весь день не было: ${absent.join(', ')}`);
    if (early.length) details.push(`Закончилось: ${early.join(', ')}`);
    return [{ store, kind: 'drop', headline, details }];
  });
}

export function share(part: string, total: number): string {
  return total ? `${Math.round((num(part) / total) * 100)}%` : '';
}

function warsawParts(now: Date): { date: string; hour: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { date: `${part('year')}-${part('month')}-${part('day')}`, hour: Number(part('hour')) };
}

export function warsawToday(now: Date): string {
  return warsawParts(now).date;
}

/** Before noon the owners look at yesterday; afterwards at today. Warsaw time. */
export function defaultReportDate(now: Date): string {
  const { date, hour } = warsawParts(now);
  return hour < 12 ? shiftDate(date, -1) : date;
}

/**
 * The day the report opens on when it is already known without ERP: from noon today
 * (the server's rule, `DEFAULT_DAY_SWITCH_HOUR`). Before noon ERP names the last day with sales.
 */
export function openingDay(now: Date): string | null {
  const { date, hour } = warsawParts(now);
  return hour >= 12 ? date : null;
}

export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function asOfTime(asOf: string | null): string | null {
  if (!asOf) return null;
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Warsaw',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(asOf));
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
